import { useState } from 'react'
import { Trash2 } from 'lucide-react'
import type { ChatSession } from '../state/types'
import { cn } from '@neo-agents/ui'

interface SessionListItemProps {
  session: ChatSession
  isActive: boolean
  showClassTitle?: boolean
  onSelect: () => void
  onDelete: () => void
}

function relativeTime(timestamp: number): string {
  const diff = Date.now() - timestamp
  const seconds = Math.floor(diff / 1000)
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h`
  const days = Math.floor(hours / 24)
  return `${days}d`
}

export function SessionListItem({
  session,
  isActive,
  showClassTitle = false,
  onSelect,
  onDelete,
}: SessionListItemProps) {
  const [hovering, setHovering] = useState(false)

  return (
    <div className="mb-1">
      <div
        role="button"
        tabIndex={0}
        className={cn(
          'group w-full flex items-center gap-2 rounded-base px-2.5 py-2 text-left text-sm transition-colors border-2 border-transparent cursor-pointer',
          isActive
            ? '!border-nb-border bg-secondary-bg text-foreground font-heading'
            : 'text-foreground/70 hover:bg-secondary-bg/50',
        )}
        onClick={onSelect}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onSelect() }}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        >
        <div className="flex-1 min-w-0">
          {showClassTitle && session.classTitle ? (
            <span className="mb-0.5 block truncate text-[10px] font-heading uppercase tracking-[0.14em] text-foreground/45">
              {session.classTitle}
            </span>
          ) : null}
          <span className="block truncate leading-snug">
            {session.name || 'New chat'}
          </span>
          {session.preview && !showClassTitle ? (
            <span className="block truncate text-[11px] text-foreground/40 leading-snug mt-0.5">
              {session.preview}
            </span>
          ) : null}
        </div>
        <div className="shrink-0 relative flex items-center justify-end w-8 h-5">
          <span
            className={cn(
              'text-[11px] text-foreground/30 tabular-nums font-base transition-opacity',
              hovering ? 'opacity-0' : 'opacity-100',
            )}
          >
            {relativeTime(session.lastUsedAt)}
          </span>
          <button
            className={cn(
              'absolute inset-0 flex items-center justify-center rounded-base text-foreground/40 hover:text-destructive transition-all',
              hovering ? 'opacity-100' : 'opacity-0 pointer-events-none',
            )}
            onClick={(e) => {
              e.stopPropagation()
              onDelete()
            }}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  )
}
